import React, { useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import { KTSVG } from 'src/_metronic/helpers';
import {
  usePotentialCustomerCreate,
  useCompanyTrustCheck,
  usePotentialCustomerContactCreate,
  useSubmitApprovalRequest,
  useCancelApprovalRequest,
} from '../service';
import ApolloContactPicker, { PendingApolloContact } from './apollo-contact-picker';
import SimilarityApprovalPanel, { ApprovalInfo, ApprovalMatchItem, SimilarityMatchCards } from './similarity-approval-panel';
import ContactEmailConflictModal, { getCrossEmailConflict, CrossEmailMatch } from 'src/app/components/ContactEmailConflictModal';

interface Props {
  show: boolean;
  handleClose: () => void;
  refetchList: () => void;
}

type TrustCheckResult = {
  guven: 'Güvenilir' | 'Güvenilmez';
  guvenilmeme_sebebi: string;
} | null;

// ApprovalInfo tipi ve onay paneli ./similarity-approval-panel'de (Düzenle modalıyla ortak)

// Duotone KTSVG ikonları (bootstrap-icons projede yüklü değil — bi sınıfları görünmez)
const ICONS = {
  ai: '/media/icons/duotone/Devices/CPU1.svg',
  check: '/media/icons/duotone/Navigation/Check.svg',
  doneCircle: '/media/icons/duotone/Code/Done-circle.svg',
  warning: '/media/icons/duotone/Code/Warning-2.svg',
  search: '/media/icons/duotone/General/Search.svg',
  star: '/media/icons/duotone/General/Star.svg',
  group: '/media/icons/duotone/Communication/Group.svg',
  building: '/media/icons/duotone/Home/Building.svg',
  shieldCheck: '/media/icons/duotone/General/Shield-check.svg',
  barcode: '/media/icons/duotone/Shopping/Barcode-scan.svg',
  contact: '/media/icons/duotone/Communication/Contact1.svg',
  send: '/media/icons/duotone/Communication/Send.svg',
  close: '/media/icons/duotone/Navigation/Close.svg',
  mail: '/media/icons/duotone/Communication/Mail.svg',
  call: '/media/icons/duotone/Communication/Call-1.svg',
  edit: '/media/icons/duotone/General/Edit.svg',
  info: '/media/icons/duotone/General/Info.svg',
  questionCircle: '/media/icons/duotone/Code/Question-circle.svg',
};

// Benzerlik taramasının kapsadığı kaynaklar (bilgi kartında gösterilir)
const SCAN_SOURCES = [
  { icon: ICONS.group, label: 'Potansiyel müşteriler' },
  { icon: ICONS.building, label: 'DIA cari kayıtları' },
  { icon: ICONS.shieldCheck, label: 'Gerçek müşteriler' },
  { icon: ICONS.barcode, label: 'VKN eşleşmesi' },
];

// Akış adım göstergesi: 1 Bilgiler → 2 Hızlı Kontrol + Kayıt → 3 Arka plan AI → Yönetici Onayı
const FlowSteps: React.FC<{ current: 1 | 2 | 3; approvalMode?: boolean }> = ({ current, approvalMode }) => {
  const steps = [
    { n: 1 as const, label: 'Firma Bilgileri' },
    { n: 2 as const, label: approvalMode ? 'Hızlı Kontrol' : 'Hızlı Kontrol + Kayıt' },
    { n: 3 as const, label: approvalMode ? 'Yönetici Onayı' : 'Arka Plan AI → Onay' },
  ];
  return (
    <div className="d-flex align-items-center justify-content-center mb-7">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          {i > 0 && (
            <div
              className={`mx-3 border-top border-2 ${current > steps[i - 1].n ? 'border-primary' : 'border-gray-300'}`}
              style={{ width: 48 }}
            ></div>
          )}
          <div className="d-flex align-items-center gap-2">
            <span className="symbol symbol-35px">
              <span
                className={`symbol-label fs-7 fw-bold ${
                  current > s.n
                    ? 'bg-primary text-white'
                    : current === s.n
                    ? 'bg-light-primary text-primary border border-primary'
                    : 'bg-light text-gray-500'
                }`}
              >
                {current > s.n ? <KTSVG path={ICONS.check} className="svg-icon-4 svg-icon-white" /> : s.n}
              </span>
            </span>
            <span className={`fs-8 fw-semibold d-none d-md-inline ${current >= s.n ? 'text-gray-800' : 'text-gray-400'}`}>{s.label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

// Süreç açıklaması — form altındaki bilgi kartı ve kayıt sonrası bilgi modalı ortak kullanır.
// Kullanıcı Kaydet'e basınca ne olacağını (hızlı kontrol → kayıt → arka plan AI →
// otomatik onay → e-posta) açık ve anlaşılır biçimde görmeli.
const PROCESS_STEPS: { icon: string; title: string; text: React.ReactNode }[] = [
  {
    icon: '/media/icons/duotone/General/Search.svg',
    title: 'Hızlı benzerlik kontrolü (saniyeler içinde)',
    text: (
      <>
        Kaydet'e bastığınızda VKN ve ünvan benzerliğine bakılır. Benzer kayıt <b>yoksa kayıt hemen açılır</b>; varsa benzer kayıtlar
        adlarıyla gösterilir: firma zaten kayıtlıysa vazgeçebilir, farklı firmaysa devam edebilirsiniz (birebir eşleşmede açıklamanızla
        onaya gönderilir).
      </>
    ),
  },
  {
    icon: '/media/icons/duotone/Devices/CPU1.svg',
    title: 'Yapay zekâ destekli tarama (arka planda, beklemezsiniz)',
    text: (
      <>
        Kayıt açıldıktan sonra potansiyeller, DIA cari kayıtları ve gerçek müşteriler yapay zekâ ile ayrıntılı taranır. Bu sırada
        kayıt listenizde <span className="badge badge-light-info fs-9">AI taraması sürüyor</span> etiketiyle görünür; ilgili kişi, ziyaret,
        fırsat gibi tüm işlemleri yapabilirsiniz.
      </>
    ),
  },
  {
    icon: '/media/icons/duotone/Code/Time-schedule.svg',
    title: 'Tarama bitince kayıt otomatik olarak yönetici onayına düşer',
    text: (
      <>
        Benzerlik bulunsun ya da bulunmasın her yeni kayıt, tarama sonucuyla birlikte yönetici onayına sunulur; etiket{' '}
        <span className="badge badge-light-warning fs-9">Onay bekliyor</span> olur. Yönetici farklı bir benzerlik tespit ederse kayıt{' '}
        <b>açıklamasıyla birlikte reddedilebilir</b> (kayıt pasife alınır, itiraz edebilirsiniz).
      </>
    ),
  },
  {
    icon: '/media/icons/duotone/Communication/Mail.svg',
    title: 'Onay / red sonucu e-posta ile bildirilir',
    text: (
      <>
        Sonuç size e-posta ve CRM bildirimi olarak gelir. Onaylanırsa etiket kalkar; yalnızca <b>gerçek müşteriye dönüştürme</b> onaydan
        sonra yapılabilir.
      </>
    ),
  },
];

const ProcessStepsList: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className={`d-flex flex-column ${compact ? 'gap-3' : 'gap-4'}`}>
    {PROCESS_STEPS.map((st, i) => (
      <div key={i} className="d-flex align-items-start">
        <span className={`symbol ${compact ? 'symbol-30px' : 'symbol-40px'} me-3 flex-shrink-0`}>
          <span className="symbol-label bg-white border">
            <KTSVG path={st.icon} className={`${compact ? 'svg-icon-4' : 'svg-icon-3'} svg-icon-primary`} />
          </span>
        </span>
        <div>
          <div className={`fw-bold text-gray-900 ${compact ? 'fs-7' : 'fs-6'}`}>
            {i + 1}. {st.title}
          </div>
          <div className={`text-gray-700 ${compact ? 'fs-8' : 'fs-7'} mt-1`}>{st.text}</div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Kayıt sonrası bilgi modalı: kayıt açıldı, AI taraması arka planda; ne olacağı
 * adım adım anlatılır. Ana modal kapandıktan sonra gösterilir.
 */
const PostCreateInfoModal: React.FC<{ info: { name: string; id?: number } | null; onClose: () => void }> = ({ info, onClose }) => (
  <Modal show={!!info} onHide={onClose} centered size="lg">
    <Modal.Header closeButton>
      <Modal.Title className="d-flex align-items-center">
        <KTSVG path={ICONS.doneCircle} className="svg-icon-1 svg-icon-success me-2" />
        Kayıt oluşturuldu — AI taraması arka planda sürüyor
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="rounded border border-success border-dashed bg-light-success p-4 mb-5">
        <div className="fw-bold text-gray-900 fs-6">"{info?.name}" potansiyel müşteri olarak listenize eklendi.</div>
        <div className="text-gray-700 fs-7 mt-1">
          Hızlı kontrolde birebir eşleşme bulunmadı. Beklemenize gerek yok; yapay zekâ destekli ayrıntılı tarama arka planda devam
          ediyor ve sonucu size bildirilecek.
        </div>
      </div>
      <div className="fw-bold text-gray-800 fs-7 mb-3">Bundan sonra ne olacak?</div>
      <ProcessStepsList compact />
    </Modal.Body>
    <Modal.Footer>
      <button type="button" className="btn btn-primary" onClick={onClose}>
        Anladım
      </button>
    </Modal.Footer>
  </Modal>
);

export default function PotentialCustomerCreate({ show, handleClose, refetchList }: Props) {
  const [formData, setFormData] = useState({
    name: '',
    note: '',
    tax_no: '',
    postal_code: '',
  });
  const [trustCheckResult, setTrustCheckResult] = useState<TrustCheckResult>(null);
  const [apolloOpen, setApolloOpen] = useState(false);
  const [apolloContacts, setApolloContacts] = useState<PendingApolloContact[]>([]);
  const [approvalInfo, setApprovalInfo] = useState<ApprovalInfo | null>(null);
  const [approvalNote, setApprovalNote] = useState('');
  // Birebir olmayan benzer adlı kayıtlar: kayıt açılmadan önce kullanıcıya gösterilir
  // (görüp vazgeçebilsin); "Yine de Kaydet" confirm_similar ile yeniden gönderir.
  const [similarMatches, setSimilarMatches] = useState<ApprovalMatchItem[] | null>(null);
  // Birebir eşleşme panelinde ayrıca listelenen diğer benzerler
  const [approvalSimilar, setApprovalSimilar] = useState<ApprovalMatchItem[]>([]);
  // Kayıt sonrası bilgi modalı (kayıt açıldı, AI taraması arka planda)
  const [postCreateInfo, setPostCreateInfo] = useState<{ name: string; id?: number } | null>(null);

  const { mutateAsync: createContactAsync, isLoading: isContactSaving } = usePotentialCustomerContactCreate();

  // Apollo "İletişim Bilgisi Topla" kaydında çakışan kişilerin sıralı uyarı
  // kuyruğu; müşteri oluşturulduktan sonra ana modal kapanır, kuyruk üstte kalır
  const [apolloConflicts, setApolloConflicts] = useState<{
    customerId: number;
    queue: { contact: PendingApolloContact; matches: CrossEmailMatch[] }[];
    total: number;
  } | null>(null);

  const resetForm = () => {
    setFormData({ name: '', note: '', tax_no: '', postal_code: '' });
    setTrustCheckResult(null);
    setApolloContacts([]);
    setApprovalInfo(null);
    setApprovalNote('');
    setSimilarMatches(null);
    setApprovalSimilar([]);
  };

  // Kayıt oluştuktan sonra Apollo'dan seçilen kişileri ilgili kişi olarak yaz.
  // Çapraz e-posta/uzantı çakışması olanlar (409) kaydedilmez; çağırana döner
  // ve sıralı uyarı kuyruğunda kullanıcıya tek tek sorulur.
  const saveApolloContacts = async (potentialCustomerId: number) => {
    let saved = 0;
    const failed: string[] = [];
    const conflicts: { contact: PendingApolloContact; matches: CrossEmailMatch[] }[] = [];
    for (const c of apolloContacts) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await createContactAsync({
          potential_customer_id: potentialCustomerId,
          full_name: c.full_name,
          email: c.email,
          phone: c.phone,
          title: c.title,
          notes: c.notes,
        });
        saved += 1;
      } catch (e: any) {
        const conflict = getCrossEmailConflict(e);
        if (conflict) conflicts.push({ contact: c, matches: conflict.matches || [] });
        else failed.push(c.full_name);
      }
    }
    if (saved > 0) toast.success(`${saved} Apollo kişisi ilgili kişilere eklendi`);
    if (failed.length > 0) toast.error(`Eklenemeyen kişiler: ${failed.join(', ')}`);
    return conflicts;
  };

  // Kuyruğun başındaki kişiyi düşür; kuyruk boşalınca modal kapanır
  const advanceApolloConflictQueue = () => {
    setApolloConflicts((prev) => (!prev || prev.queue.length <= 1 ? null : { ...prev, queue: prev.queue.slice(1) }));
  };

  const handleApolloConflictContinue = async () => {
    if (!apolloConflicts) return;
    const head = apolloConflicts.queue[0];
    if (!head) return;
    try {
      await createContactAsync({
        potential_customer_id: apolloConflicts.customerId,
        full_name: head.contact.full_name,
        email: head.contact.email,
        phone: head.contact.phone,
        title: head.contact.title,
        notes: head.contact.notes,
        force_cross_duplicate: true,
      });
      toast.success(`${head.contact.full_name} ilgili kişilere eklendi`);
    } catch (e) {
      toast.error(`${head.contact.full_name} eklenemedi`);
    }
    advanceApolloConflictQueue();
  };

  // Hızlı kontrolde birebir eşleşme bulunduğunda onay paneline geçiş
  const handleApprovalRequired = (data: any): boolean => {
    if (!data?.approval_required) return false;
    const info: ApprovalInfo = {
      request_id: data.request_id,
      already_pending: Boolean(data.already_pending),
      matches: data.matches || [],
      summary: data.summary || null,
      ai_status: data.ai_status,
      ai_model: data.ai_model || null,
    };
    if (info.already_pending) {
      toast.info('Bu firma için onay talebiniz zaten beklemede. Sonuç e-posta ile bildirilecek.');
      handleClose();
      resetForm();
      return true;
    }
    setSimilarMatches(null);
    setApprovalSimilar(data.similar_matches || []);
    setApprovalInfo(info);
    return true;
  };

  const onSuccess = async (response: any) => {
    // Hızlı kontrolde VKN / ünvan birebir eşleşmesi varsa onay paneline geçilir.
    if (handleApprovalRequired(response?.data)) return;

    // Birebir eşleşme yok ama benzer adlı kayıtlar var: kayıt henüz AÇILMADI,
    // liste gösterilir; kullanıcı "Yine de Kaydet" derse kayıt açılır.
    if (response?.data?.similar_found) {
      setSimilarMatches(response.data.matches || []);
      return;
    }

    const createdName = response?.data?.name || formData.name;
    toast.success('Potansiyel müşteri eklendi; AI benzerlik taraması arka planda sürüyor.');
    const newId = response?.data?.id;
    let conflicts: { contact: PendingApolloContact; matches: CrossEmailMatch[] }[] = [];
    if (apolloContacts.length > 0) {
      if (newId) {
        conflicts = await saveApolloContacts(newId);
      } else {
        toast.error('Müşteri ID alınamadığı için Apollo kişileri eklenemedi.');
      }
    }
    handleClose();
    refetchList();
    resetForm();
    // Çakışan kişiler modal kapandıktan sonra sıralı uyarı kuyruğunda sorulur
    if (conflicts.length > 0 && newId) {
      setApolloConflicts({ customerId: newId, queue: conflicts, total: conflicts.length });
    }
    // Süreç bilgilendirmesi (arka plan tarama → otomatik onay → e-posta)
    setPostCreateInfo({ name: createdName, id: newId });
  };

  const onError = (e: any) => {
    toast.error(e?.response?.data?.message || 'Bir hata oluştu');
  };

  const { mutate: createMutate, isLoading } = usePotentialCustomerCreate(onSuccess, onError);

  const { mutate: submitApproval, isLoading: isSubmittingApproval } = useSubmitApprovalRequest(
    async (response: any) => {
      // Kayıt onaya gönderildiği anda "Onay bekliyor" etiketiyle açılır (created_potential_id);
      // Apollo kişileri de doğrudan bu kayda eklenir.
      const newId = response?.data?.created_potential_id;
      toast.success(newId ? 'Kayıt "Onay bekliyor" etiketiyle oluşturuldu ve onaya gönderildi.' : 'Talebiniz onaya gönderildi. Sonuç e-posta ile bildirilecek.');
      let conflicts: { contact: PendingApolloContact; matches: CrossEmailMatch[] }[] = [];
      if (apolloContacts.length > 0) {
        if (newId) {
          conflicts = await saveApolloContacts(newId);
        } else {
          toast.info('Apollo kişileri kayıt onaylandıktan sonra tekrar eklenebilir.');
        }
      }
      handleClose();
      refetchList();
      resetForm();
      if (conflicts.length > 0 && newId) {
        setApolloConflicts({ customerId: newId, queue: conflicts, total: conflicts.length });
      }
    },
    (e: any) => toast.error(e?.response?.data?.message || 'Talep onaya gönderilemedi'),
  );

  const { mutate: cancelApproval, isLoading: isCancellingApproval } = useCancelApprovalRequest(
    () => {
      // Taslak silindi; kullanıcı forma dönüp adı düzeltebilir
      setApprovalInfo(null);
      setApprovalNote('');
    },
    () => {
      setApprovalInfo(null);
      setApprovalNote('');
    },
  );

  // Güvenilirlik kontrolü hook'u
  const onTrustCheckSuccess = (response: any) => {
    const data = response?.data?.data;
    if (data) {
      setTrustCheckResult(data);
    }
  };
  const onTrustCheckError = (e: any) => {
    toast.error(e?.response?.data?.message || 'Güvenilirlik sorgusu yapılırken bir hata oluştu');
    setTrustCheckResult(null);
  };
  const { isLoading: isTrustCheckLoading, mutate: checkTrust } = useCompanyTrustCheck(onTrustCheckSuccess, onTrustCheckError);

  // Tek adımlı akış (28 Eyl 2026): Kaydet → sunucuda hızlı deterministik kontrol
  // (VKN / ünvan birebir). Temizse kayıt hemen açılır, AI taraması arka planda
  // koşar ve bitince otomatik onaya düşer; birebir eşleşme varsa onay paneli açılır.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Lütfen müşteri adını girin');
      return;
    }
    createMutate(formData);
  };

  const handleModalClose = () => {
    resetForm();
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleModalClose} centered size="xl" className={apolloOpen ? 'pcc-hidden' : undefined} backdropClassName={apolloOpen ? 'pcc-hidden' : undefined}>
        {/* Apollo modalı açıkken bu modal (ve backdrop'u) gizlenir; iki modal üst üste
          binince görüntü karışıyordu. Unmount edilmediği için form state'i korunur. */}
        <style>{`.pcc-hidden { display: none !important; }`}</style>
        <Modal.Header closeButton>
          <Modal.Title>
            {approvalInfo ? 'Benzerlik Tespit Edildi — Onay Gerekli' : similarMatches ? 'Benzer Kayıtlar Bulundu' : 'Potansiyel Müşteri Ekle'}
          </Modal.Title>
        </Modal.Header>
        {!approvalInfo && similarMatches && (
          <>
            <Modal.Body>
              <FlowSteps current={2} />
              <div className="d-flex align-items-center rounded border border-dashed border-warning bg-light-warning p-4 mb-5">
                <span className="symbol symbol-50px me-4 flex-shrink-0">
                  <span className="symbol-label bg-warning">
                    <KTSVG path={ICONS.search} className="svg-icon-2x svg-icon-white" />
                  </span>
                </span>
                <div>
                  <div className="fw-bold fs-5 text-gray-900">
                    "{formData.name}" için {similarMatches.length} benzer kayıt bulundu
                  </div>
                  <div className="fs-7 text-gray-700 mt-1">
                    <b>Kayıt henüz açılmadı.</b> Aşağıdaki listeyi kontrol edin: eklemek istediğiniz firma bunlardan biriyse <b>Vazgeç</b>'e
                    basın. Farklı bir firma olduğundan eminseniz <b>Yine de Kaydet</b> ile devam edin; kayıt açılır, yapay zekâ taraması arka
                    planda tamamlanır ve kayıt yönetici onayına düşer.
                  </div>
                </div>
              </div>
              <div className="fw-bold text-gray-800 mb-3">Benzer bulunan kayıtlar</div>
              <SimilarityMatchCards matches={similarMatches} maxHeight={360} />
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-light" disabled={isLoading} onClick={() => setSimilarMatches(null)}>
                Vazgeç
              </button>
              <button
                type="button"
                className="btn btn-warning"
                disabled={isLoading}
                onClick={() => createMutate({ ...formData, confirm_similar: true })}
              >
                {isLoading ? (
                  <span className="indicator-progress d-block">
                    Kaydediliyor... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                ) : (
                  <span className="d-inline-flex align-items-center">
                    <KTSVG path={ICONS.check} className="svg-icon-3 svg-icon-white me-1" />
                    Farklı Firma — Yine de Kaydet
                  </span>
                )}
              </button>
            </Modal.Footer>
          </>
        )}
        {approvalInfo && (
          <>
            <Modal.Body>
              <FlowSteps current={3} approvalMode />

              <SimilarityApprovalPanel
                info={approvalInfo}
                title={<>"{formData.name}" için benzer kayıtlar bulundu</>}
                description={
                  <>
                    Hızlı kontrolde VKN veya ünvan <b>birebir</b> eşleşen kayıt var. Eklemek istiyorsanız talebiniz yönetici onayına sunulur. Kayıt{' '}
                    <b>hemen sizin adınıza açılır</b> ve <span className="badge badge-light-warning fs-9">Onay bekliyor</span> etiketiyle listenizde
                    görünür; ilgili kişi, ziyaret, fırsat gibi tüm işlemleri yapabilirsiniz (yalnızca müşteriye dönüştürme onaydan sonra). Yapay zekâ
                    destekli ayrıntılı tarama arka planda tamamlanıp onay talebinize eklenir. Yönetici onaylarsa etiket kalkar, farklı bir benzerlik
                    görürse açıklamasıyla reddeder; sonuç size e-posta ile bildirilir.
                  </>
                }
              />

              {approvalSimilar.length > 0 && (
                <div className="mb-5">
                  <div className="fw-bold text-gray-800 mb-3">Diğer benzer kayıtlar</div>
                  <SimilarityMatchCards matches={approvalSimilar} maxHeight={220} />
                </div>
              )}

              {/* Açıklama */}
              <div className="mb-2">
                <label className="form-label fw-semibold">
                  Açıklamanız <span className="text-gray-500 fw-normal fs-8">(onaylayacak yöneticiye iletilir — opsiyonel)</span>
                </label>
                <textarea
                  className="form-control form-control-solid"
                  rows={3}
                  placeholder="Bu kaydı neden eklemek istediğinizi açıklayabilirsiniz. Örn: mevcut kayıt farklı şube/lokasyon, firma el değiştirdi..."
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-light"
                disabled={isSubmittingApproval || isCancellingApproval}
                onClick={() => cancelApproval(approvalInfo.request_id)}
              >
                {isCancellingApproval ? 'Vazgeçiliyor...' : 'Vazgeç'}
              </button>
              <button
                type="button"
                className="btn btn-warning"
                disabled={isSubmittingApproval || isCancellingApproval}
                onClick={() => submitApproval({ id: approvalInfo.request_id, note: approvalNote })}
              >
                {isSubmittingApproval ? (
                  <span className="indicator-progress d-block">
                    Gönderiliyor... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                ) : (
                  <span className="d-inline-flex align-items-center">
                    <KTSVG path={ICONS.send} className="svg-icon-3 svg-icon-white me-1" />
                    Onaya Gönder
                  </span>
                )}
              </button>
            </Modal.Footer>
          </>
        )}
        {!approvalInfo && !similarMatches && (
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <FlowSteps current={isLoading ? 2 : 1} />
            <div className="mb-4">
              <label className="form-label required">Müşteri Adı</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control form-control-solid"
                  placeholder="Müşteri adı veya firma ünvanı"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    setTrustCheckResult(null);
                  }}
                />
                <button
                  type="button"
                  className="btn btn-info"
                  disabled={!formData.name || formData.name.length < 2 || isTrustCheckLoading}
                  onClick={() => {
                    if (formData.name && formData.name.length >= 2) {
                      checkTrust(formData.name);
                    }
                  }}
                >
                  {isTrustCheckLoading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : 'Finansal Risk Araştırması'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!formData.name || formData.name.length < 2}
                  title="Apollo'da bu firmayı bulup çalışanlarının iletişim bilgilerini toplayın"
                  onClick={() => setApolloOpen(true)}
                >
                  📩 İletişim Bilgisi Topla
                  {apolloContacts.length > 0 && <span className="badge badge-circle badge-light ms-2">{apolloContacts.length}</span>}
                </button>
              </div>
              {/* Güvenilirlik Sorgu Sonucu */}
              {trustCheckResult && (
                <div className={`mt-2 p-3 rounded ${trustCheckResult.guven === 'Güvenilir' ? 'bg-light-success' : 'bg-light-danger'}`}>
                  <div className="d-flex align-items-center">
                    <KTSVG
                      path={trustCheckResult.guven === 'Güvenilir' ? ICONS.doneCircle : ICONS.warning}
                      className={`svg-icon-2 me-2 ${trustCheckResult.guven === 'Güvenilir' ? 'svg-icon-success' : 'svg-icon-danger'}`}
                    />
                    <span className={`fw-bold ${trustCheckResult.guven === 'Güvenilir' ? 'text-success' : 'text-danger'}`}>{trustCheckResult.guven}</span>
                  </div>
                  {trustCheckResult.guvenilmeme_sebebi && <div className="mt-2 text-gray-700 fs-7">{trustCheckResult.guvenilmeme_sebebi}</div>}
                </div>
              )}

              {/* Apollo'dan seçilen ilgili kişiler — kayıt oluşunca otomatik eklenecek */}
              {apolloContacts.length > 0 && (
                <div className="mt-3 p-4 rounded bg-light-primary border border-primary border-dashed">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                      <KTSVG path={ICONS.group} className="svg-icon-2 svg-icon-primary me-2" />
                      <div>
                        <span className="fw-bold text-primary fs-6">Apollo'dan seçilen kişiler</span>
                        <span className="text-gray-500 fs-8 ms-2">({apolloContacts.length} kişi)</span>
                      </div>
                    </div>
                    <button type="button" className="btn btn-sm btn-light-primary d-inline-flex align-items-center" onClick={() => setApolloOpen(true)}>
                      <KTSVG path={ICONS.edit} className="svg-icon-6 me-1" />
                      Düzenle
                    </button>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {apolloContacts.map((c) => (
                      <div key={c.key} className="d-flex align-items-center rounded-2 px-3 py-2" style={{ backgroundColor: 'rgba(255,255,255,0.55)' }}>
                        <div className="flex-grow-1 min-w-0">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-bold text-gray-800 fs-7">{c.full_name}</span>
                            {c.title && <span className="text-gray-500 fs-8">{c.title}</span>}
                          </div>
                          <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                            {c.email && (
                              <span className="badge badge-sm badge-light-success d-inline-flex align-items-center gap-1 py-1 px-2">
                                <KTSVG path={ICONS.mail} className="svg-icon-7 svg-icon-success" />
                                {c.email}
                              </span>
                            )}
                            {c.phone && (
                              <span className="badge badge-sm badge-light-dark d-inline-flex align-items-center gap-1 py-1 px-2">
                                <KTSVG path={ICONS.call} className="svg-icon-7" />
                                {c.phone}
                              </span>
                            )}
                            {!c.email && !c.phone && <span className="badge badge-sm badge-light text-gray-400 py-1 px-2">İletişim bilgisi yok</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-active-light-danger"
                          title="Listeden çıkar"
                          onClick={() => setApolloContacts((prev) => prev.filter((x) => x.key !== c.key))}
                        >
                          <KTSVG path={ICONS.close} className="svg-icon-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-gray-600 fs-8 d-flex align-items-center">
                    <KTSVG path={ICONS.info} className="svg-icon-6 me-1" />
                    Bu kişiler, müşteri kaydı oluşturulduktan sonra "İlgili Kişiler" listesine otomatik eklenecek.
                  </div>
                </div>
              )}

            </div>
            <div className="mb-4">
              <label className="form-label">Not</label>
              <textarea
                className="form-control form-control-solid"
                rows={3}
                placeholder="Notlar..."
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              />
            </div>
            <div className="row">
              <div className="col-md-6 mb-4">
                <label className="form-label">Vergi No</label>
                <input
                  type="text"
                  className="form-control form-control-solid"
                  placeholder="Vergi numarası"
                  value={formData.tax_no}
                  onChange={(e) => {
                    setFormData({ ...formData, tax_no: e.target.value });
                  }}
                />
              </div>
              <div className="col-md-6 mb-4">
                <label className="form-label">Posta Kodu</label>
                <input
                  type="text"
                  className="form-control form-control-solid"
                  placeholder="Posta kodu"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                />
              </div>
            </div>

            {/* Süreç bilgilendirme kartı: hızlı kontrol → kayıt → arka plan AI → otomatik onay → e-posta */}
            {isLoading ? (
              <div className="rounded border border-primary border-dashed bg-light-primary p-5 d-flex align-items-center">
                <span className="symbol symbol-50px me-4 flex-shrink-0">
                  <span className="symbol-label bg-white">
                    <KTSVG path={ICONS.search} className="svg-icon-2x svg-icon-primary" />
                  </span>
                </span>
                <div className="flex-grow-1">
                  <div className="fw-bold text-gray-900 fs-6">Hızlı benzerlik kontrolü yapılıyor…</div>
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {SCAN_SOURCES.map((s) => (
                      <span key={s.label} className="badge badge-white text-gray-700 fw-normal d-inline-flex align-items-center gap-1">
                        <KTSVG path={s.icon} className="svg-icon-7 svg-icon-primary" />
                        {s.label}
                      </span>
                    ))}
                  </div>
                  <div className="progress h-6px mt-3 bg-white">
                    <div className="progress-bar progress-bar-striped progress-bar-animated bg-primary" style={{ width: '100%' }}></div>
                  </div>
                  <div className="text-gray-600 fs-9 mt-1">Birkaç saniye sürer; birebir eşleşme yoksa kayıt hemen açılır.</div>
                </div>
              </div>
            ) : (
              <div className="rounded border border-dashed border-gray-300 bg-light p-4">
                <div className="d-flex align-items-center mb-3">
                  <KTSVG path={ICONS.info} className="svg-icon-2 svg-icon-primary me-2" />
                  <span className="fw-bold text-gray-800 fs-7">Kaydet'e bastığınızda ne olur? Beklemeniz gerekmez.</span>
                </div>
                <ProcessStepsList compact />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={handleModalClose} disabled={isLoading}>
              İptal
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading || !formData.name.trim()}>
              {isLoading ? (
                <span className="indicator-progress d-block">
                  Hızlı kontrol ve kayıt... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              ) : (
                <span className="d-inline-flex align-items-center">
                  <KTSVG path={ICONS.check} className="svg-icon-3 svg-icon-white me-1" />
                  Kaydet
                </span>
              )}
            </button>
          </Modal.Footer>
        </form>
        )}
      </Modal>

      <PostCreateInfoModal info={postCreateInfo} onClose={() => setPostCreateInfo(null)} />

      <ApolloContactPicker
        show={apolloOpen}
        companyName={formData.name}
        initialSelected={apolloContacts}
        onClose={() => setApolloOpen(false)}
        onConfirm={(contacts) => setApolloContacts(contacts)}
      />

      {/* Apollo kaydında çakışan kişiler için sıralı uyarı (Vazgeç = o kişiyi atla) */}
      <ContactEmailConflictModal
        show={!!apolloConflicts?.queue.length}
        email={apolloConflicts?.queue[0]?.contact.email || ''}
        matches={apolloConflicts?.queue[0]?.matches || []}
        progressLabel={
          apolloConflicts
            ? `Apollo kişisi ${apolloConflicts.total - apolloConflicts.queue.length + 1}/${apolloConflicts.total}: ${
                apolloConflicts.queue[0]?.contact.full_name || ''
              }`
            : undefined
        }
        onCancel={advanceApolloConflictQueue}
        onContinue={handleApolloConflictContinue}
        isSubmitting={isContactSaving}
      />
    </>
  );
}
